import React from 'react';
import { Loader2, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { SafeDatabaseUser } from '../../../types/types';
import { CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../../ui/card';
import { Button } from '../../ui/button';
import Input from '../../ui/input';
import { Alert, AlertDescription } from '../../ui/alert';
import use2FASetup from '../../../hooks/use2FASetup';

interface TwoFactorSetupProps {
  user: SafeDatabaseUser;
  onComplete: (updatedUser: SafeDatabaseUser) => void;
  onClose?: () => void;
}

/**
 * Component for setting up two-factor authentication
 */
const TwoFactorSetup: React.FC<TwoFactorSetupProps> = ({ user, onComplete, onClose }) => {
  const { loading, error, step, code, setCode, handleInitiateSetup, handleVerifyCode } =
    use2FASetup(user);

  // Handle verification and completion
  const handleVerify = async () => {
    const updatedUser = await handleVerifyCode();
    if (updatedUser) {
      onComplete(updatedUser);
    }
  };

  if (step === 'initial') {
    return (
      <>
        <CardHeader className='relative'>
          <div className='absolute right-2 top-2'>
            <Button variant='ghost' size='icon' onClick={onClose} aria-label='Close'>
              <X className='h-4 w-4' />
            </Button>
          </div>
          <CardTitle>Set Up Two-Factor Authentication</CardTitle>
          <CardDescription>Add an extra layer of security to your account</CardDescription>
        </CardHeader>
        <CardContent className='space-y-4'>
          <p className='text-sm text-muted-foreground'>
            Two-factor authentication adds an extra layer of security to your account. Once enabled,
            you&apos;ll need to enter a verification code sent to your email when you sign in.
          </p>

          <div className='bg-muted p-4 rounded-md'>
            <p className='text-sm font-medium'>
              We&apos;ll send a verification code to your email address:{' '}
              <strong>{user.email || 'No email available'}</strong>
            </p>
            {!user.email && (
              <div className='space-y-2 mt-2'>
                <p className='text-sm text-destructive'>
                  You need a valid email address to set up 2FA.
                </p>
                <Link to={`/user/${user.username}/settings`} onClick={onClose}>
                  <Button variant='outline' size='sm' className='mt-1'>
                    Add Email in Profile Settings
                  </Button>
                </Link>
              </div>
            )}
          </div>

          {error && (
            <Alert variant='destructive'>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
        </CardContent>
        <CardFooter className='flex justify-between'>
          <Button variant='outline' onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleInitiateSetup} disabled={loading || !user.email}>
            {loading && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
            Send Verification Code
          </Button>
        </CardFooter>
      </>
    );
  }

  return (
    <>
      <CardHeader className='relative'>
        <div className='absolute right-2 top-2'>
          <Button variant='ghost' size='icon' onClick={onClose} aria-label='Close'>
            <X className='h-4 w-4' />
          </Button>
        </div>
        <CardTitle>Verify Your Email</CardTitle>
        <CardDescription>Enter the verification code sent to your email</CardDescription>
      </CardHeader>
      <CardContent className='space-y-4'>
        <p className='text-sm text-muted-foreground'>
          We&apos;ve sent a verification code to your email address. Please enter the 6-digit code
          below.
        </p>

        <Input
          value={code}
          onChange={e => setCode(e.target.value.replace(/\D/g, '').substring(0, 6))}
          placeholder='Enter 6-digit code'
          maxLength={6}
          className='text-center text-xl tracking-widest'
        />

        {error && (
          <Alert variant='destructive'>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
      </CardContent>
      <CardFooter className='flex justify-between'>
        <Button variant='outline' onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={handleVerify} disabled={loading || code.length !== 6}>
          {loading && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
          Verify & Enable 2FA
        </Button>
      </CardFooter>
    </>
  );
};

export default TwoFactorSetup;
